import React, { useState, useEffect, useRef } from 'react';
import {
    View, Text, StyleSheet, FlatList, TouchableOpacity,
    ActivityIndicator, Image, Platform, Dimensions, Animated
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUserProfile, getUserPosts, toggleFollow, toggleLike } from '../../../api/social';
import { getOrCreateDirectRoom } from '../../../api/chat';
import PostCard from '../../../components/social/feed/PostCard';
import UserAvatar from '../../../components/social/shared/UserAvatar';
import { useAuth } from '../../../contexts/AuthContext';
import { FeedSkeleton } from '../../../components/shared/SkeletonLoader';
import HandleEditSheet from '../../../components/social/feed/HandleEditSheet';

const { width } = Dimensions.get('window');

const UserProfileScreen = ({ route, navigation }) => {
    const { userId } = route.params;
    const { user: currentUser } = useAuth();
    const insets = useSafeAreaInsets();
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [allPosts, setAllPosts] = useState([]);
    const [isStartingChat, setIsStartingChat] = useState(false);
    const [handleEditVisible, setHandleEditVisible] = useState(false);
    const [viewableItemIds, setViewableItemIds] = useState([]);

    const { data: profile, isLoading: isProfileLoading } = useQuery({
        queryKey: ['userProfile', userId],
        queryFn: () => getUserProfile(userId)
    });

    const { data: postData, isLoading: isPostsLoading } = useQuery({
        queryKey: ['userPosts', userId, page],
        queryFn: () => getUserPosts(userId, page),
        keepPreviousData: true
    });

    useEffect(() => {
        if (postData?.posts) {
            if (page === 1) setAllPosts(postData.posts);
            else setAllPosts(prev => [...prev, ...postData.posts]);
        }
    }, [postData, page]);

    const followMutation = useMutation({
        mutationFn: () => toggleFollow(userId),
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: ['userProfile', userId] });
            const previousProfile = profile;
            queryClient.setQueryData(['userProfile', userId], old => {
                if (!old) return old;
                return {
                    ...old,
                    isFollowing: !old.isFollowing,
                    followersCount: old.isFollowing ? Math.max(0, old.followersCount - 1) : old.followersCount + 1
                };
            });
            return { previousProfile };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousProfile) {
                queryClient.setQueryData(['userProfile', userId], context.previousProfile);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries(['userProfile', userId]);
            queryClient.invalidateQueries(['feed']);
        }
    });

    const likeMutation = useMutation({
        mutationFn: toggleLike,
        onSuccess: () => {
            queryClient.invalidateQueries(['userPosts', userId]);
        }
    });

    const startChat = async () => {
        if (!profile || isStartingChat) return;
        setIsStartingChat(true);
        try {
            const room = await getOrCreateDirectRoom(userId);
            navigation.navigate('ChatRoom', {
                roomId: room.id,
                otherUser: profile,
                room
            });
        } catch (error) {
            console.error('Start chat error:', error);
        } finally {
            setIsStartingChat(false);
        }
    };

    const isMe = currentUser?.id === userId;

    // ── Viewability Config (Auto-play/pause) ─────────────────────────────
    const viewabilityConfig = useRef({
        itemVisiblePercentThreshold: 50,
    }).current;

    const onViewableItemsChanged = useRef(({ viewableItems }) => {
        const ids = viewableItems.map(item => item.key);
        setViewableItemIds(ids);
    }).current;

    const renderHeader = () => (
        <View style={styles.headerArea}>
            <View style={styles.profileSection}>
                {/* Left: Avatar */}
                <View style={styles.avatarContainer}>
                    <UserAvatar source={profile?.profileImageUrl} name={profile?.name} size={76} />
                </View>

                {/* Right: Info & Actions */}
                <View style={styles.profileInfo}>
                    <View style={styles.nameRow}>
                        <Text style={styles.profileName} numberOfLines={1}>{profile?.name}</Text>
                        <View style={styles.handleContainer}>
                            <Text style={styles.profileHandle} numberOfLines={1}>
                                @{profile?.socialHandle || profile?.username || profile?.email?.split('@')[0]}
                            </Text>
                            {isMe && (
                                <TouchableOpacity 
                                    style={styles.editHandleBtn}
                                    onPress={() => setHandleEditVisible(true)}
                                >
                                    <Ionicons name="pencil" size={12} color="#1D9BF0" />
                                    <Text style={styles.editHandleText}>Edit</Text>
                                </TouchableOpacity>
                            )}
                        </View>
                    </View>

                    {/* Compact Stats */}
                    <View style={styles.statsRow}>
                        <View style={styles.statItem}>
                            <Text style={styles.statCount}>{profile?.postsCount || 0}</Text>
                            <Text style={styles.statLabel}>Posts</Text>
                        </View>
                        <View style={styles.statItem}>
                            <Text style={styles.statCount}>{profile?.followersCount || 0}</Text>
                            <Text style={styles.statLabel}>Followers</Text>
                        </View>
                        <View style={styles.statItem}>
                            <Text style={styles.statCount}>{profile?.followingCount || 0}</Text>
                            <Text style={styles.statLabel}>Following</Text>
                        </View>
                    </View>

                    {/* Actions */}
                    {!isMe && (
                        <View style={styles.actionRow}>
                            <TouchableOpacity
                                style={[styles.followBtn, profile?.isFollowing && styles.followingBtn]}
                                onPress={() => followMutation.mutate()}
                            >
                                <Text style={[styles.followBtnText, profile?.isFollowing && styles.followingBtnText]}>
                                    {profile?.isFollowing ? 'Following' : 'Follow'}
                                </Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={styles.chatBtn} onPress={startChat}>
                                {isStartingChat ? (
                                    <ActivityIndicator size="small" color="#0891b2" />
                                ) : (
                                    <Ionicons name="chatbubble-ellipses-outline" size={18} color="#0891b2" />
                                )}
                            </TouchableOpacity>
                        </View>
                    )}
                </View>
            </View>

            <View style={styles.tabsRow}>
                <TouchableOpacity style={styles.activeTab}>
                    <Text style={styles.activeTabText}>Posts</Text>
                    <View style={styles.tabIndicator} />
                </TouchableOpacity>
                <TouchableOpacity style={styles.inactiveTab}>
                    <Text style={styles.inactiveTabText}>Media</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.inactiveTab}>
                    <Text style={styles.inactiveTabText}>Liked</Text>
                </TouchableOpacity>
            </View>
        </View>
    );

    return (
        <View style={styles.container}>
            {/* ── Fixed Lean Header ── */}
            <LinearGradient
                colors={['#0891b2', '#0369a1']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={[styles.fixedHeader, { paddingTop: insets.top + 15 }]}
            >
                <View style={styles.headerContent}>
                    <TouchableOpacity
                        style={styles.backBtn}
                        onPress={() => navigation.goBack()}
                    >
                        <Ionicons name="arrow-back" size={22} color="#FFFFFF" />
                    </TouchableOpacity>
                    <View style={styles.headerCenter}>
                        <Text style={styles.headerTitle}>{profile?.name || 'Profile'}</Text>
                    </View>
                    <TouchableOpacity style={styles.moreBtn}>
                        <Ionicons name="ellipsis-vertical" size={20} color="#FFFFFF" />
                    </TouchableOpacity>
                </View>
            </LinearGradient>

            {isProfileLoading && page === 1 ? (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color="#0891b2" />
                </View>
            ) : (
                <FlatList
                    data={allPosts}
                    renderItem={({ item }) => {
                        const isVisible = viewableItemIds.includes(item.id.toString());
                        return (
                            <PostCard
                                post={item}
                                isLiked={item.likes?.length > 0}
                                isVisible={isVisible}
                                onLike={() => likeMutation.mutate(item.id)}
                                onComment={() => navigation.navigate('PostDetail', { postId: item.id })}
                            />
                        );
                    }}
                    keyExtractor={item => item.id.toString()}
                    ListHeaderComponent={renderHeader}
                    contentContainerStyle={styles.listContent}
                    viewabilityConfig={viewabilityConfig}
                    onViewableItemsChanged={onViewableItemsChanged}
                    onEndReached={() => {
                        if (postData && page < postData.totalPages) setPage(p => p + 1);
                    }}
                    ListEmptyComponent={
                        !isPostsLoading && (
                            <View style={styles.emptyPosts}>
                                <Text style={styles.emptyText}>No posts yet</Text>
                            </View>
                        )
                    }
                />
            )}

            <HandleEditSheet
                visible={handleEditVisible}
                currentHandle={profile?.socialHandle}
                onClose={() => setHandleEditVisible(false)}
                onSaved={() => queryClient.invalidateQueries(['userProfile', userId])}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },
    /* Fixed header style */
    fixedHeader: {
        height: Platform.OS === 'ios' ? 100 : 90,
        justifyContent: 'center',
    },
    headerContent: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingBottom: 4,
        gap: 12,
    },
    backBtn: {
        width: 34,
        height: 34,
        borderRadius: 10,
        backgroundColor: 'rgba(255,255,255,0.15)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerCenter: {
        flex: 1,
    },
    headerTitle: {
        fontSize: 17,
        fontWeight: '800',
        color: '#FFFFFF',
    },

    moreBtn: {
        padding: 5,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    headerArea: {
        backgroundColor: '#FFFFFF',
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: '#E1E8ED',
    },
    profileSection: {
        flexDirection: 'row',
        padding: 16,
        alignItems: 'flex-start',
    },
    avatarContainer: {
        marginRight: 16,
    },
    profileInfo: {
        flex: 1,
    },
    nameRow: {
        marginBottom: 8,
    },
    profileName: {
        fontSize: 20,
        fontWeight: '800',
        color: '#0F1419',
    },
    profileHandle: {
        fontSize: 14,
        color: '#536471',
    },
    handleContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    editHandleBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 3,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
        backgroundColor: 'rgba(29,155,240,0.1)',
    },
    editHandleText: {
        fontSize: 11,
        fontWeight: '700',
        color: '#1D9BF0',
    },
    statsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 4,
        gap: 16,
    },
    statItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    statCount: {
        fontSize: 15,
        fontWeight: '700',
        color: '#0F1419',
    },
    statLabel: {
        fontSize: 13,
        color: '#536471',
    },
    statDivider: {
        width: 1,
        height: 14,
        backgroundColor: '#E5E7EB',
    },
    actionRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 14,
        gap: 8,
    },
    followBtn: {
        backgroundColor: '#0F1419',
        paddingHorizontal: 16,
        paddingVertical: 7,
        borderRadius: 20,
        minWidth: 100,
        alignItems: 'center',
    },
    followingBtn: {
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#CFD9DE',
    },
    followBtnText: {
        color: '#FFFFFF',
        fontWeight: '700',
        fontSize: 14,
    },
    followingBtnText: {
        color: '#0F1419',
    },
    chatBtn: {
        width: 34,
        height: 34,
        borderRadius: 17,
        backgroundColor: '#FFFFFF',
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#CFD9DE',
    },
    tabsRow: {
        flexDirection: 'row',
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: '#EFF3F4',
    },
    activeTab: {
        flex: 1,
        alignItems: 'center',
        paddingVertical: 14,
        position: 'relative',
    },
    activeTabText: {
        fontSize: 15,
        fontWeight: '700',
        color: '#0F1419',
    },
    tabIndicator: {
        position: 'absolute',
        bottom: 0,
        height: 4,
        width: 40,
        backgroundColor: '#1D9BF0',
        borderRadius: 2,
    },
    inactiveTab: {
        flex: 1,
        alignItems: 'center',
        paddingVertical: 14,
    },
    inactiveTabText: {
        fontSize: 15,
        fontWeight: '600',
        color: '#536471',
    },
    listContent: {
        paddingBottom: 20,
    },
    emptyPosts: {
        padding: 40,
        alignItems: 'center',
    },
    emptyText: {
        color: '#536471',
        fontSize: 15,
    }
});

export default UserProfileScreen;
